/**
 * VoyagePhasedInterface - Clean phase-based voyage management
 * Phase 1: PRE-DEPARTURE (Alongside preparing)
 * Phase 2: UNDERWAY (At sea)
 * Phase 3: ALONGSIDE (Arrived at destination)
 */

import React, { useState, useEffect } from 'react';
import { devLog } from '../../../../utils/devLog';
import { Anchor, Navigation, Flag, ChevronRight, CheckCircle, Ship, Layers } from 'lucide-react';
import PreDeparturePhase from './voyage-phases/PreDeparturePhase';
import PreDeparturePhaseExpanded from './voyage-phases/PreDeparturePhaseExpanded';
import SimplifiedPreDeparturePhase from './voyage-phases/SimplifiedPreDeparturePhase';
import UnderwayPhase from './voyage-phases/UnderwayPhase';
import UnderwayPhaseExpanded from './voyage-phases/UnderwayPhaseExpanded';
import SimplifiedUnderwayPhase from './voyage-phases/SimplifiedUnderwayPhase';
import AlongsidePhase from './voyage-phases/AlongsidePhase';

const VoyagePhasedInterface = ({ 
  activeVoyage,
  onStartVoyage,
  onUpdateVoyage,
  onEndVoyage,
  onAddLogEntry,
  logEntries = [],
  crew = [],
  passagePlans = [],
  currentVessel,
  useExpandable = false // TEMPORARILY DEFAULT TO FALSE due to performance issue
}) => {
  // Voyage phases
  const PHASES = {
    PRE_DEPARTURE: 'pre_departure',
    UNDERWAY: 'underway',
    ALONGSIDE: 'alongside'
  };

  const [currentPhase, setCurrentPhase] = useState(PHASES.PRE_DEPARTURE);
  const [expandedPhase, setExpandedPhase] = useState(PHASES.PRE_DEPARTURE);
  
  // Vessel type selector
  const [vesselType, setVesselType] = useState('coastal'); // 'inland', 'coastal', 'commercial'
  const [complexityMode, setComplexityMode] = useState('simple'); // 'simple', 'advanced'
  
  // Phase completion states
  const [phaseCompletion, setPhaseCompletion] = useState({
    voyageDetails: false,
    crewAssigned: false,
    preDepartureChecks: false,
    weatherAssessed: false,
    passagePlanReviewed: false,
    safetyBriefing: false
  });

  // Determine current phase based on voyage status
  // RE-ENABLED: Phase logic restored per BRIEF-20251225-OPS-V3
  useEffect(() => {
    devLog('VoyagePhasedInterface: activeVoyage changed:', activeVoyage);
    devLog('VoyagePhasedInterface: activeVoyage status:', activeVoyage?.status);

    if (!activeVoyage) {
      devLog('No active voyage, setting to PRE_DEPARTURE');
      setCurrentPhase(PHASES.PRE_DEPARTURE);
      setExpandedPhase(PHASES.PRE_DEPARTURE);
    } else if (activeVoyage.status === 'active') {
      devLog('Voyage is active, setting to UNDERWAY');
      setCurrentPhase(PHASES.UNDERWAY);
      setExpandedPhase(PHASES.UNDERWAY);
    } else if (activeVoyage.status === 'completed' || activeVoyage.status === 'alongside') {
      devLog('Voyage is completed/alongside, setting to ALONGSIDE');
      setCurrentPhase(PHASES.ALONGSIDE);
      setExpandedPhase(PHASES.ALONGSIDE);
    } else {
      devLog('Voyage status not recognized, staying in PRE_DEPARTURE');
      // Default to PRE_DEPARTURE if status is not set
      setCurrentPhase(PHASES.PRE_DEPARTURE);
      setExpandedPhase(PHASES.PRE_DEPARTURE);
    }
  }, [activeVoyage, PHASES]);

  // Check if ready to transition phases
  const canLetGo = () => {
    return phaseCompletion.voyageDetails &&
           phaseCompletion.crewAssigned &&
           phaseCompletion.preDepartureChecks &&
           phaseCompletion.weatherAssessed;
  };

  // Handle pre-departure completion
  const handlePreDepartureComplete = () => {
    devLog('Pre-departure phase completed');
    // Move to underway phase when pre-departure is complete
    setCurrentPhase(PHASES.UNDERWAY);
    setExpandedPhase(PHASES.UNDERWAY);
  };

  const handleAllFast = () => {
    // Record all fast in log
    const allFastEntry = {
      type: 'ALL_FAST',
      category: 'navigation',
      summary: 'All Fast - Made Fast',
      description: `Arrived ${activeVoyage.destinationPort}`,
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage.id
    };
    onAddLogEntry(allFastEntry);

    // Update voyage status
    onUpdateVoyage({
      ...activeVoyage,
      status: 'alongside',
      actualArrivalTime: new Date().toISOString()
    });

    setCurrentPhase(PHASES.ALONGSIDE);
    setExpandedPhase(PHASES.ALONGSIDE);
  };

  const handleCompleteVoyage = () => {
    onEndVoyage(activeVoyage.id);
  };

  // Phase indicator component
  const PhaseIndicator = ({ phase, label, icon: Icon, isActive, isComplete, isExpanded, onClick }) => (
    <div 
      onClick={onClick}
      className={`flex items-center p-2 sm:p-3 rounded-lg cursor-pointer transition-all ${
        isActive ? 'bg-blue-50 border-2 border-blue-500' :
        isComplete ? 'bg-green-50 border-2 border-green-500' :
        'bg-gray-50 border-2 border-gray-200'
      }`}
    >
      <div className={`p-1.5 sm:p-2 rounded-full ${
        isActive ? 'bg-blue-500' :
        isComplete ? 'bg-green-500' :
        'bg-gray-300'
      }`}>
        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
      </div>
      <div className="ml-2 sm:ml-3 flex-1">
        <p className={`text-xs sm:text-sm font-medium ${
          isActive ? 'text-blue-900' :
          isComplete ? 'text-green-900' :
          'text-gray-600'
        }`}>
          {label}
        </p>
      </div>
      {isComplete && <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />}
      <ChevronRight className={`hidden sm:block h-5 w-5 ml-2 text-gray-400 transition-transform ${
        isExpanded ? 'rotate-90' : ''
      }`} />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Vessel Type and Complexity Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Ship className="h-5 w-5 text-blue-600" />
            <select
              value={vesselType}
              onChange={(e) => setVesselType(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value="inland">Inland Waterway</option>
              <option value="coastal">Coastal Workboat</option>
              <option value="commercial">Commercial Vessel</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-gray-500" />
            <button
              onClick={() => setComplexityMode(complexityMode === 'simple' ? 'advanced' : 'simple')}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {complexityMode === 'simple' ? 'Switch to Advanced' : 'Switch to Simple'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Phase Progress Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-3 gap-4">
          <PhaseIndicator
            phase={PHASES.PRE_DEPARTURE}
            label="Pre-Departure"
            icon={Anchor}
            isActive={currentPhase === PHASES.PRE_DEPARTURE}
            isComplete={currentPhase !== PHASES.PRE_DEPARTURE}
            isExpanded={expandedPhase === PHASES.PRE_DEPARTURE}
            onClick={() => setExpandedPhase(PHASES.PRE_DEPARTURE)}
          />
          <PhaseIndicator
            phase={PHASES.UNDERWAY}
            label="Underway"
            icon={Navigation}
            isActive={currentPhase === PHASES.UNDERWAY}
            isComplete={currentPhase === PHASES.ALONGSIDE}
            isExpanded={expandedPhase === PHASES.UNDERWAY}
            onClick={() => setExpandedPhase(PHASES.UNDERWAY)}
          />
          <PhaseIndicator
            phase={PHASES.ALONGSIDE}
            label="Alongside"
            icon={Flag}
            isActive={currentPhase === PHASES.ALONGSIDE}
            isComplete={false}
            isExpanded={expandedPhase === PHASES.ALONGSIDE}
            onClick={() => setExpandedPhase(PHASES.ALONGSIDE)}
          />
        </div>
      </div>

      {/* Phase Content */}
      {expandedPhase === PHASES.PRE_DEPARTURE && (
        complexityMode === 'simple' ? (
          <SimplifiedPreDeparturePhase
            activeVoyage={activeVoyage}
            onUpdateVoyage={onUpdateVoyage}
            onAddLogEntry={onAddLogEntry}
            onPreDepartureComplete={handlePreDepartureComplete}
            crew={crew}
            currentVessel={currentVessel}
            vesselType={vesselType}
          />
        ) : (
          useExpandable ? (
            <PreDeparturePhaseExpanded
              activeVoyage={activeVoyage}
              onUpdateVoyage={onUpdateVoyage}
              onLetGo={handlePreDepartureComplete}
              crew={crew}
              passagePlans={passagePlans}
              currentVessel={currentVessel}
            />
          ) : (
            <PreDeparturePhase
              activeVoyage={activeVoyage}
              phaseCompletion={phaseCompletion}
              setPhaseCompletion={setPhaseCompletion}
              onLetGo={handlePreDepartureComplete}
              canLetGo={canLetGo()}
            />
          )
        )
      )}

      {expandedPhase === PHASES.UNDERWAY && (
        complexityMode === 'simple' ? (
          <SimplifiedUnderwayPhase
            activeVoyage={activeVoyage}
            logEntries={logEntries}
            onUpdateVoyage={onUpdateVoyage}
            onAllFast={handleAllFast}
            onAddLogEntry={onAddLogEntry}
            crew={crew}
            currentVessel={currentVessel}
            vesselType={vesselType}
          />
        ) : (
          useExpandable ? (
            <UnderwayPhaseExpanded
              activeVoyage={activeVoyage}
              logEntries={logEntries}
              onUpdateVoyage={onUpdateVoyage}
              onAllFast={handleAllFast}
              onAddLogEntry={onAddLogEntry}
              crew={crew}
              currentVessel={currentVessel}
            />
          ) : (
            <UnderwayPhase
              activeVoyage={activeVoyage}
              logEntries={logEntries}
              onAllFast={handleAllFast}
              onAddLogEntry={onAddLogEntry}
            />
          )
        )
      )}

      {expandedPhase === PHASES.ALONGSIDE && (
        <AlongsidePhase
          activeVoyage={activeVoyage}
          logEntries={logEntries}
          onCompleteVoyage={handleCompleteVoyage}
        />
      )}
    </div>
  );
};

export default VoyagePhasedInterface;